import type { ColumnDef } from "@tanstack/react-table";
import { AlertCircle, LoaderCircle, Pencil, Plus, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { canManageFactory } from "@/app/guards";
import { useObraId } from "@/app/useObraId";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { ExportButton } from "@/components/ui/ExportButton";
import { type CampoFiltro, FiltrosBar } from "@/components/ui/filtros-bar";
import { ImportButton } from "@/components/ui/ImportButton";
import { Modal } from "@/components/ui/modal";
import { ApiError } from "@/lib/api";
import { useMe } from "@/lib/auth";
import { useEquipos } from "@/lib/equipos";
import { AVISO_FILTRADO_LOCAL, mensajeErrorLista, useFiltrosURL } from "@/lib/filtros";
import { CLAVES_FUENTES } from "@/lib/filtros/fuentes";
import {
  type Fuente,
  TIPO_FUENTE_LABEL,
  useCreateFuente,
  useDeleteFuente,
  useFuentes,
  useUpdateFuente,
} from "@/lib/fuentes";
import { FuenteForm, type FuenteFormValues } from "./FuenteForm";

// Etiqueta de estado (activa/inactiva): es el accessor de la columna, del que tira la
// ordenación. Ni el filtro ni el badge la usan —el filtro `activo` de la FiltrosBar es
// booleano y con sus propias etiquetas, y la `cell` pinta su propio texto—.
function estadoLabel(fuente: Fuente): string {
  return fuente.activo ? "Activa" : "Inactiva";
}

// toInput normaliza los valores del formulario al payload del API (descripción vacía ⇒ null).
function toInput(values: FuenteFormValues) {
  const descripcion = values.descripcion.trim();
  return {
    id_equipo: values.id_equipo,
    nombre_fuente: values.nombre_fuente.trim(),
    tipo: values.tipo,
    descripcion: descripcion === "" ? null : descripcion,
    activo: values.activo,
  };
}

/**
 * FuentesPage lista las fuentes de energía con su equipo, tipo y estado, y ofrece el CRUD
 * completo (alta/edición en modal con chained-add, borrado con confirmación y protección
 * de historial). La tabla usa el DataTable de react-table (#34) SIN buscador en memoria
 * (`sinBuscador`): el texto libre y los filtros por tipo y estado son del SERVIDOR y viven en
 * la FiltrosBar (#247), y la tabla solo ordena y pagina, así que sus `accessorFn` alimentan
 * la celda, la ordenación o ambas, nunca una búsqueda (#346). (Fuentes aún no tiene endpoint
 * de import.)
 */
export function FuentesPage() {
  const { data: me } = useMe();
  const obraId = useObraId();
  const puedeGestionar = me != null && canManageFactory(me, obraId);

  // Los filtros viven en la URL (#247): compartible, recargable y con el servidor filtrando.
  const { filtros, setFiltros } = useFiltrosURL(CLAVES_FUENTES);
  const {
    data: fuentes,
    isPending,
    isError,
    baseVacia,
    avisoSinConexion,
    codigoError,
    camposError,
  } = useFuentes(obraId, filtros);
  const filas = fuentes ?? [];
  const { data: equipos } = useEquipos(obraId);
  const crear = useCreateFuente(obraId);
  const editar = useUpdateFuente(obraId);
  const borrar = useDeleteFuente(obraId);

  const [formState, setFormState] = useState<
    { mode: "nueva" } | { mode: "editar"; fuente: Fuente } | null
  >(null);
  const [aBorrar, setABorrar] = useState<Fuente | null>(null);

  const listaEquipos = equipos ?? [];
  const sinEquipos = listaEquipos.length === 0;

  const columns = useMemo<ColumnDef<Fuente, unknown>[]>(() => {
    const cols: ColumnDef<Fuente, unknown>[] = [
      {
        accessorKey: "codigo_fuente",
        header: "Código",
        cell: ({ row }) => (
          <span className="font-medium text-slate-900 tabular-nums">
            {row.original.codigo_fuente}
          </span>
        ),
      },
      {
        accessorKey: "nombre_fuente",
        header: "Nombre",
        cell: ({ row }) => <span className="text-slate-900">{row.original.nombre_fuente}</span>,
      },
      {
        id: "equipo",
        accessorFn: (f) => `${f.codigo_equipo} · ${f.nombre_equipo}`,
        header: "Equipo",
        cell: ({ getValue }) => <span className="text-slate-500">{getValue<string>()}</span>,
        meta: { className: "hidden sm:table-cell" },
      },
      {
        id: "tipo",
        accessorFn: (f) => TIPO_FUENTE_LABEL[f.tipo],
        header: "Tipo",
        cell: ({ getValue }) => <span className="text-slate-500">{getValue<string>()}</span>,
        meta: { className: "hidden md:table-cell" },
      },
      {
        id: "estado",
        accessorFn: (f) => estadoLabel(f),
        header: "Estado",
        cell: ({ row }) => (
          <span
            className={`inline-flex rounded-full px-2.5 py-0.5 font-medium text-sm ${
              row.original.activo ? "bg-green-100 text-green-800" : "bg-slate-200 text-slate-600"
            }`}
          >
            {row.original.activo ? "Activa" : "Inactiva"}
          </span>
        ),
      },
    ];
    if (puedeGestionar) {
      cols.push({
        id: "acciones",
        header: () => <span className="sr-only">Acciones</span>,
        enableSorting: false,
        enableGlobalFilter: false,
        meta: { className: "text-right", stopRowClick: true },
        cell: ({ row }) => (
          <div className="flex justify-end gap-1">
            <Button
              variant="ghost"
              size="icon"
              aria-label={`Editar ${row.original.nombre_fuente}`}
              onClick={() => setFormState({ mode: "editar", fuente: row.original })}
            >
              <Pencil className="h-5 w-5" aria-hidden="true" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              aria-label={`Eliminar ${row.original.nombre_fuente}`}
              onClick={() => setABorrar(row.original)}
            >
              <Trash2 className="h-5 w-5 text-red-600" aria-hidden="true" />
            </Button>
          </div>
        ),
      });
    }
    return cols;
  }, [puedeGestionar]);

  // Campos de la barra de filtros. Los valores son los del API (ids y enums), no las
  // etiquetas; los equipos salen del listado relacionado que la página ya carga.
  const campos = useMemo<CampoFiltro[]>(
    () => [
      { tipo: "texto", nombre: "q", etiqueta: "Buscar", placeholder: "Buscar fuentes…" },
      {
        tipo: "select",
        nombre: "equipo",
        etiqueta: "Equipo",
        opciones: listaEquipos.map((e) => ({
          valor: String(e.id),
          etiqueta: `${e.codigo_equipo} · ${e.nombre_equipo}`,
        })),
      },
      {
        tipo: "select",
        nombre: "tipo",
        etiqueta: "Tipo",
        opciones: Object.entries(TIPO_FUENTE_LABEL)
          .map(([valor, etiqueta]) => ({ valor, etiqueta }))
          .sort((a, b) => a.etiqueta.localeCompare(b.etiqueta)),
      },
      {
        tipo: "booleano",
        nombre: "activo",
        etiqueta: "Activa",
        etiquetaSi: "Activa",
        etiquetaNo: "Inactiva",
      },
    ],
    [listaEquipos],
  );

  function cerrarForm() {
    setFormState(null);
  }

  async function onSubmitForm(values: FuenteFormValues, addAnother: boolean) {
    if (formState?.mode === "editar") {
      await editar.mutateAsync({ id: formState.fuente.id, input: toInput(values) });
      cerrarForm();
      return;
    }
    await crear.mutateAsync(toInput(values));
    // Chained-add: si el usuario pulsó «Guardar y añadir otra», el modal permanece abierto
    // (el formulario limpia sus campos manteniendo el equipo); si no, se cierra.
    if (!addAnother) {
      cerrarForm();
    }
  }

  async function confirmarBorrado() {
    if (!aBorrar) {
      return;
    }
    try {
      await borrar.mutateAsync(aBorrar.id);
      setABorrar(null);
    } catch {
      // El fallo (p. ej. 409 FUENTE_CON_HISTORIAL) se refleja en borrar.error y se muestra
      // en el modal; se mantiene abierto para que el usuario lo lea.
    }
  }

  const borrarConHistorial =
    borrar.error instanceof ApiError && borrar.error.code === "FUENTE_CON_HISTORIAL";

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex flex-col">
          <h1 className="font-bold text-2xl text-slate-900 tracking-tight">Fuentes de energía</h1>
          <p className="text-base text-slate-500">Fuentes de energía de cada equipo.</p>
        </div>
        <div className="flex flex-wrap justify-end gap-2">
          <ImportButton tipo="fuentes" etiqueta="fuentes" obraId={obraId} />
          {/* La exportación arrastra los filtros de la URL (#249 D9): se descarga lo mismo
              que se está viendo. */}
          <ExportButton tipo="fuentes" obraId={obraId} filtros={filtros} />
          {puedeGestionar ? (
            <Button onClick={() => setFormState({ mode: "nueva" })} disabled={sinEquipos}>
              <Plus className="h-5 w-5" aria-hidden="true" />
              Nueva fuente
            </Button>
          ) : null}
        </div>
      </div>

      {puedeGestionar && sinEquipos ? (
        <Alert variant="info">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
          <span>Crea primero un equipo para poder dar de alta fuentes.</span>
        </Alert>
      ) : null}

      {isError ? (
        <Alert variant="destructive">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
          <span>
            {mensajeErrorLista(
              { codigoError, camposError },
              "No se pudieron cargar las fuentes. Inténtalo de nuevo.",
            )}
          </span>
        </Alert>
      ) : null}

      {isPending ? (
        <div className="flex items-center justify-center gap-2 py-10 text-slate-500">
          <LoaderCircle className="h-6 w-6 animate-spin" aria-hidden="true" />
          Cargando fuentes…
        </div>
      ) : null}

      {!isPending && !isError && baseVacia ? (
        <div className="rounded-2xl border border-slate-200 border-dashed bg-white px-4 py-12 text-center text-slate-500">
          Aún no hay fuentes.
          {puedeGestionar && !sinEquipos ? " Crea la primera con «Nueva fuente»." : ""}
        </div>
      ) : null}

      {!baseVacia ? (
        <>
          <FiltrosBar
            campos={campos}
            valor={filtros}
            onChange={setFiltros}
            aviso={avisoSinConexion ? AVISO_FILTRADO_LOCAL : undefined}
          />
          {!isPending ? (
            <DataTable
              columns={columns}
              data={filas}
              sinBuscador
              onRowClick={
                puedeGestionar ? (fuente) => setFormState({ mode: "editar", fuente }) : undefined
              }
            />
          ) : null}
        </>
      ) : null}

      {/* Alta / edición */}
      <Modal
        open={formState !== null}
        onClose={cerrarForm}
        title={formState?.mode === "editar" ? "Editar fuente" : "Nueva fuente"}
      >
        {formState !== null ? (
          <FuenteForm
            equipos={listaEquipos}
            codigo={formState.mode === "editar" ? formState.fuente.codigo_fuente : undefined}
            showAddAnother={formState.mode === "nueva"}
            defaultValues={
              formState.mode === "editar"
                ? {
                    id_equipo: formState.fuente.id_equipo,
                    nombre_fuente: formState.fuente.nombre_fuente,
                    tipo: formState.fuente.tipo,
                    descripcion: formState.fuente.descripcion ?? "",
                    activo: formState.fuente.activo,
                  }
                : {
                    id_equipo: listaEquipos[0]?.id ?? 0,
                    nombre_fuente: "",
                    tipo: "ELECTRICA",
                    descripcion: "",
                    activo: true,
                  }
            }
            submitLabel={formState.mode === "editar" ? "Guardar cambios" : "Crear fuente"}
            pending={crear.isPending || editar.isPending}
            onSubmit={onSubmitForm}
            onCancel={cerrarForm}
          />
        ) : null}
      </Modal>

      {/* Confirmación de borrado */}
      <Modal open={aBorrar !== null} onClose={() => setABorrar(null)} title="Eliminar fuente">
        <p className="text-base text-slate-600">
          ¿Seguro que quieres eliminar la fuente <strong>{aBorrar?.nombre_fuente}</strong>? Esta
          acción la da de baja.
        </p>
        {borrar.isError ? (
          <Alert variant="destructive">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
            <span>
              {borrarConHistorial
                ? "La fuente tiene puntos de aislamiento asociados. Desactívala en vez de eliminarla."
                : "No se pudo eliminar la fuente. Inténtalo de nuevo."}
            </span>
          </Alert>
        ) : null}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => setABorrar(null)} disabled={borrar.isPending}>
            Cancelar
          </Button>
          <Button
            variant="destructive"
            onClick={() => void confirmarBorrado()}
            disabled={borrar.isPending}
          >
            {borrar.isPending ? (
              <LoaderCircle className="h-5 w-5 animate-spin" aria-hidden="true" />
            ) : (
              <Trash2 className="h-5 w-5" aria-hidden="true" />
            )}
            Eliminar
          </Button>
        </div>
      </Modal>
    </div>
  );
}
